/**
 * IndexedDB Cache Helper Utility - By John Bellew - Infinia.ie - last updated 10/07/2025
 * 
 * A specialized IndexedDB utility for JSON file caching with advanced features:
 * - High-performance JSON file caching system
 * - Expiration-based cache invalidation
 * - Automatic cache cleanup and management
 * - Modern async/await interface with IDB library
 * - Type-safe storage operations for JSON data
 * - Memory optimization for large datasets
 * - Error handling for all database operations
 * 
 * Features:
 * - JSON file caching with expiration times
 * - Automatic cache invalidation after expiry
 * - High-performance data retrieval
 * - Memory-efficient storage management
 * - Configurable expiration times (default: 30 minutes)
 * - Automatic database schema initialization
 * - Error resilience for storage operations
 * 
 * Cache Management:
 * - Time-based expiration (configurable per item)
 * - Automatic cleanup of expired entries
 * - Memory optimization for large JSON files
 * - Fast retrieval for non-expired cached data
 * 
 * Database Structure:
 * - Database: 'json-cache-db'
 * - Store: 'json-files'
 * - Key: String identifier for cached file
 * - Value: JSON data or timestamped cache object
 * 
 * Usage:
 * import { setItemInIndexedDB, getItemFromIndexedDB } from './indexedDBHelper';
 * await setItemInIndexedDB('myFile', jsonData);
 * const data = await getItemFromIndexedDB('myFile');
 * 
 * @requires idb Modern IndexedDB wrapper library
 * @returns {Object} Cache management methods for JSON file storage
 */
import { openDB } from 'idb';

const dbPromise = openDB('json-cache-db', 1, {
  upgrade(db) {
    db.createObjectStore('json-files');
  },
});

// Function to set items in IndexedDB
export const setItemInIndexedDB = async (key, value) => {
  const db = await dbPromise;
  await db.put('json-files', value, key);
};

// Function to get items from IndexedDB
export const getItemFromIndexedDB = async (key) => {
  const db = await dbPromise;
  return db.get('json-files', key);
};

// Function to set cached item with expiration
export const setCachedItemWithExpiry = async (key, value, expiryInMinutes = 30) => {
  const timestampedValue = { value, expiry: Date.now() + expiryInMinutes * 60000 };
  await setItemInIndexedDB(key, timestampedValue);
};

// Function to get cached item with expiration check
export const getCachedItemWithExpiry = async (key) => {
  const cachedData = await getItemFromIndexedDB(key);
  if (cachedData && Date.now() < cachedData.expiry) {
    return cachedData.value;
  }
  return null; // Return null if expired or missing
};
